/**
 * WebSocket Client for Real-Time Notifications
 *
 * Manages WebSocket connection to the backend for real-time notification delivery.
 * Features:
 * - Automatic reconnection with exponential backoff
 * - Ping/pong keepalive
 * - Event subscription system
 * - Graceful degradation (falls back to polling if WS fails)
 */

export interface BroadcastMessage {
  type: 'notification' | 'data_update' | 'pong';
  payload?: {
    notificationId?: string;
    notificationType?: string;
    entityType?: string;
    entityId?: string;
    title?: string;
    message?: string;
    priority?: string;
    actionLink?: string;
    data?: Record<string, unknown>;
  };
  timestamp: string | number;
}

type MessageHandler = (message: BroadcastMessage) => void;

export class WebSocketClient {
  private ws: WebSocket | null = null;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private reconnectDelay = 1000; // Base delay in ms
  private listeners: Map<string, Set<MessageHandler>> = new Map();
  private pingInterval: number | null = null;
  private connectionState: 'disconnected' | 'connecting' | 'connected' = 'disconnected';
  private getToken: () => string | null;
  private wsUrl: string;
  private isIntentionalDisconnect = false;

  constructor(getToken: () => string | null, wsUrl?: string) {
    this.getToken = getToken;
    this.wsUrl = wsUrl || import.meta.env.VITE_WS_URL || '';
  }

  /**
   * Get current connection state
   */
  getState(): 'disconnected' | 'connecting' | 'connected' {
    return this.connectionState;
  }

  /**
   * Connect to WebSocket server
   */
  connect(): void {
    if (this.connectionState !== 'disconnected') {
      console.log('[WebSocket] Already connecting or connected');
      return;
    }

    const token = this.getToken();
    if (!token) {
      console.log('[WebSocket] No token available, skipping connection');
      return;
    }

    if (!this.wsUrl) {
      console.warn('[WebSocket] VITE_WS_URL not configured, skipping connection');
      return;
    }

    this.connectionState = 'connecting';
    this.isIntentionalDisconnect = false;

    try {
      this.ws = new WebSocket(`${this.wsUrl}?token=${token}`);

      this.ws.onopen = () => {
        // Check if we were told to disconnect while connecting
        if (this.isIntentionalDisconnect) {
          this.ws?.close(1000, 'Client disconnect');
          return;
        }
        this.connectionState = 'connected';
        this.reconnectAttempts = 0;
        this.startPingInterval();
        this.notifyListeners('_connected', {
          type: 'notification',
          payload: {},
          timestamp: Date.now().toString(),
        });
      };

      this.ws.onmessage = (event) => {
        try {
          const message: BroadcastMessage = JSON.parse(event.data);
          this.notifyListeners(message.type, message);
        } catch (error) {
          console.error('[WebSocket] Failed to parse message:', error);
        }
      };

      this.ws.onclose = () => {
        this.connectionState = 'disconnected';
        this.stopPingInterval();
        this.notifyListeners('_disconnected', {
          type: 'notification',
          payload: {},
          timestamp: Date.now().toString(),
        });
        if (!this.isIntentionalDisconnect) {
          this.handleReconnect();
        }
      };

      this.ws.onerror = () => {
        // Suppress error logging for intentional disconnects (e.g., React StrictMode cleanup)
        if (!this.isIntentionalDisconnect) {
          console.warn('[WebSocket] Connection error');
        }
      };
    } catch (error) {
      console.error('[WebSocket] Connection failed:', error);
      this.connectionState = 'disconnected';
      this.handleReconnect();
    }
  }

  /**
   * Disconnect from WebSocket server
   */
  disconnect(): void {
    this.isIntentionalDisconnect = true;
    this.stopPingInterval();
    this.reconnectAttempts = this.maxReconnectAttempts; // Prevent auto-reconnect
    if (this.ws) {
      // Only close if the connection is open
      // For CONNECTING state, the onopen handler will close it
      if (this.ws.readyState === WebSocket.OPEN) {
        this.ws.close(1000, 'Client disconnect');
      }
      // For CONNECTING, CLOSING, or CLOSED states - let the handlers deal with it
      this.ws = null;
    }
    this.connectionState = 'disconnected';
  }

  /**
   * Subscribe to a message type
   * Returns an unsubscribe function
   */
  subscribe(type: string, callback: MessageHandler): () => void {
    if (!this.listeners.has(type)) {
      this.listeners.set(type, new Set());
    }
    this.listeners.get(type)!.add(callback);

    return () => {
      this.listeners.get(type)?.delete(callback);
    };
  }

  /**
   * Notify all listeners of a message
   */
  private notifyListeners(type: string, message: BroadcastMessage): void {
    // Notify specific type listeners
    this.listeners.get(type)?.forEach(callback => {
      try {
        callback(message);
      } catch (error) {
        console.error('[WebSocket] Listener error:', error);
      }
    });

    // Notify wildcard listeners
    this.listeners.get('*')?.forEach(callback => {
      try {
        callback(message);
      } catch (error) {
        console.error('[WebSocket] Wildcard listener error:', error);
      }
    });
  }

  /**
   * Handle reconnection with exponential backoff
   */
  private handleReconnect(): void {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      return;
    }

    // Exponential backoff with jitter
    const jitter = Math.random() * 1000;
    const delay = Math.min(
      this.reconnectDelay * Math.pow(2, this.reconnectAttempts) + jitter,
      30000 // Max 30 seconds
    );
    this.reconnectAttempts++;

    setTimeout(() => {
      // Re-check if we should reconnect (user might have navigated away)
      if (this.connectionState === 'disconnected') {
        this.connect();
      }
    }, delay);
  }

  /**
   * Start ping interval to keep connection alive
   */
  private startPingInterval(): void {
    // Send ping every 5 minutes to keep connection alive
    // API Gateway WebSocket has a 10-minute idle timeout
    this.pingInterval = window.setInterval(() => {
      if (this.ws?.readyState === WebSocket.OPEN) {
        this.ws.send(JSON.stringify({ action: 'ping' }));
      }
    }, 5 * 60 * 1000);
  }

  /**
   * Stop ping interval
   */
  private stopPingInterval(): void {
    if (this.pingInterval) {
      clearInterval(this.pingInterval);
      this.pingInterval = null;
    }
  }

  /**
   * Manually trigger a reconnection (e.g., after token refresh)
   */
  reconnect(): void {
    this.disconnect();
    this.reconnectAttempts = 0; // Reset attempts for manual reconnect
    this.connect();
  }
}

// Singleton instance
let wsClientInstance: WebSocketClient | null = null;

export function getWebSocketClient(getToken: () => string | null): WebSocketClient {
  if (!wsClientInstance) {
    wsClientInstance = new WebSocketClient(getToken);
  }
  return wsClientInstance;
}

export function resetWebSocketClient(): void {
  if (wsClientInstance) {
    wsClientInstance.disconnect();
    wsClientInstance = null;
  }
}
